import MaterialTable from '@material-table/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import { makeStyles } from '@material-ui/styles';
import { isPast, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import gql from 'graphql-tag';
import { observer } from 'mobx-react';
import React, { useContext, useState, useMemo } from 'react';

import { ActionButton } from 'src/components/pages/pageElements/tasks/ActionButton';
import { PatientTaskResults } from 'src/components/pages/pageElements/tasks/PatientTaskResults';
import { useTasks } from 'src/components/pages/pageElements/tasks/useTasks';
import { ApolloClientContext } from 'src/data/ApolloClientContext';
import { PatientTasksQuery } from 'src/generated/gql/graphql';
import Colors from 'src/nightingale/Colors';
import { InlineHelp } from 'src/nightingale/components/ChartElement/InlineHelp';
import { SelectControl } from 'src/nightingale/components/ChartProperty/controls/Select/SelectControl';
import { PageColumn } from 'src/nightingale/components/common/PageColumn/PageColumn';
import { PageTitle } from 'src/nightingale/components/common/PageTitle/PageTitle';
import { SelectOption } from 'src/nightingale/types/types';

export const CREATE_TASK = gql`
  mutation CreateTask($id: ID!, $interactionKey: String!) {
    createTask(patientId: $id, interactionKey: $interactionKey)
  }
`;

const createTaskHelpMessage = `Clicking Send below will immediately send the selected task to the patient. Do not send more than one of each task to a patient.`;

/**
 * @todo Pull these task definitions from mongo.
 * These interaction keys need to remain in sync with the tasks defined in the
 * chartDefinitions.allInteractionFlowMappings collection in mongo.
 * See https://github.com/bouldercare/boulder/compare/PAX-1352-query-flow-mappings-for-task-launcher
 * for an incomplete implementation on populating this list using a query through
 * the staffBff.
 * @see {@link https://bouldercare.atlassian.net/browse/PAX-1352}
 */
const INTERACTION_KEYS = [
  {
    label: 'ID Card Upload',
    value: 'id_card_upload_task',
  },
  {
    label: 'Insurance Card Upload',
    value: 'insurance_card_upload_task',
  },
  {
    label: 'Letter To Someone Starting Boulder',
    value: 'letter_to_someone_starting_boulder',
  },
  {
    label: 'Nutrition',
    value: 'nutrition_fbpt',
  },
  {
    label: 'Quick Start Feedback',
    value: 'quickStartFeedbackFbpt',
  },
  {
    label: 'SMS Consent',
    value: 'sms_consents_task',
  },
  {
    label: 'Social Background',
    value: 'social_background',
  },
  {
    label: 'Patient App Feedback',
    value: 'patient_app_feedback',
  },
];

const PatientTasks: React.FC<{ patientId: string }> = ({ patientId }) => {
  const styles = useStyles();
  const interactionByKey = useMemo(() => {
    return INTERACTION_KEYS.reduce((acc, curr) => ({ ...acc, [curr.value]: curr }), {});
  }, []);
  const [interactionKey, setInteractionKey] = useState<string | null | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const { apolloClient } = useContext(ApolloClientContext);
  const { tasks, mutateTasks } = useTasks(patientId);

  const handleCreateTask = async () => {
    if (!apolloClient) {
      return;
    }

    if (interactionKey) {
      setIsLoading(true);
      try {
        await apolloClient.mutate({
          mutation: CREATE_TASK,
          variables: { id: patientId, interactionKey },
        });
        setInteractionKey(undefined);
        // This will mutate the local swr cache for the GET_TASKS query
        // which will cause a re-validation of the tasks data. We do this
        // to force the task table to update after a new task has been
        // created via the task launcher.
        mutateTasks({ tasks });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const formatDate = (date: string) => {
    return formatInTimeZone(
      parseISO(date),
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      'yyyy-MM-dd zzz',
    );
  };

  const getCompletedAtText = (rowData: PatientTasksQuery['tasks'][number]) => {
    if (rowData.completedAt) {
      return formatDate(rowData.completedAt);
    }

    const isTaskExpired = rowData.expiresAt ? isPast(new Date(rowData.expiresAt)) : false;
    if (isTaskExpired) {
      return (
        <i>
          N/A - expired
          <br />
          {formatDate(rowData.expiresAt as string)}
        </i>
      );
    }

    return null;
  };

  return (
    <PageColumn>
      <section className={styles.section}>
        <PageTitle>Patient Tasks</PageTitle>

        <Card className={styles.cardContainer}>
          <CardHeader
            title="Add a Patient Task"
            classes={{ title: styles.header, root: styles.cardHeaderContainer }}
          />
          <CardContent className={styles.cardContentContainer}>
            <InlineHelp helpText={createTaskHelpMessage} />
            <SelectControl
              options={INTERACTION_KEYS}
              onChangeValue={(option: SelectOption) => {
                if (option?.value) {
                  setInteractionKey(option.value);
                } else if (interactionKey !== undefined) {
                  setInteractionKey(undefined);
                }
              }}
              allowMultiple={false}
              value={interactionKey && interactionByKey[interactionKey]}
              label="Select a task to send to the patient"
            />
            <ActionButton
              isLoading={isLoading}
              disabled={!interactionKey}
              onClick={handleCreateTask}
            >
              Send Task to Patient
            </ActionButton>
          </CardContent>
        </Card>
      </section>
      <section className={styles.section}>
        <MaterialTable
          data={tasks}
          columns={[
            {
              title: 'Task Type',
              field: 'flow.label',
              render: rowData => <div>{rowData.flow.label}</div>,
              sorting: false,
            },
            {
              title: 'Created At',
              field: 'createdAt',
              render: rowData => (
                <div>{rowData.createdAt ? formatDate(rowData.createdAt) : null}</div>
              ),
              defaultSort: 'desc',
            },
            {
              title: 'Completed At',
              field: 'completedAt',
              render: rowData => <div>{getCompletedAtText(rowData)}</div>,
            },
          ]}
          isLoading={isLoading}
          detailPanel={[
            rowData => ({
              disabled: !rowData.completedAt,
              render: () => (
                <PatientTaskResults
                  patientId={patientId}
                  taskId={rowData.taskId}
                  flowName={rowData.flow.name}
                  rowData={rowData}
                />
              ),
            }),
          ]}
          onRowClick={(event, rowData, togglePanel) =>
            rowData?.completedAt ? togglePanel?.() : () => {}
          }
          options={{
            toolbar: false,
            paging: false,
            filtering: false,
            search: false,
            headerStyle: { display: 'default' },
            sorting: true,
          }}
        />
      </section>
    </PageColumn>
  );
};

const useStyles = makeStyles({
  cardContainer: {
    backgroundColor: Colors.White,
    borderRadius: 0,
    boxShadow: 'none',
    marginTop: 30,
    overflow: 'visible',
    padding: 16,
  },
  cardHeaderContainer: {
    padding: 0,
  },
  cardContentContainer: {
    padding: 0,
    position: 'relative',
    '&:last-child': { paddingBottom: 0 },
  },
  section: {
    marginBottom: 60,
  },
  header: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '17.4px',
    '&, &.Mui-error, &.Mui-focused': {
      color: Colors.BlueSpruce,
    },
    transform: 'scale(1)',
    position: 'static',
  },
  submitButton: {
    marginTop: 10,
  },
});

export default observer(PatientTasks);
